import { useEffect, useState } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	ANALYTICS_OPERATIONAL_SUBJECT,
	type AnalyticsOrigin,
} from '../../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../analytics/useSnippetAnalytics.ts';
import * as ConversationalAiClient from '../../services/conversationalAiClient.ts';
import { SnippetMetadataError } from '../../services/conversationalAiClient.ts';
import type {
	SnippetMetadataSuggestion,
	SnippetMetadataResponse,
} from '../../services/types/conversationalAiClientTypes.ts';
import { wrapAdfWithDocNode } from '../../utils/adfUtils.ts';
import { reportError } from '../../utils/reportError.ts';

function pickBestSuggestion(suggestions: SnippetMetadataSuggestion[]): SnippetMetadataSuggestion {
	return suggestions.reduce((a, b) => (a.confidence >= b.confidence ? a : b));
}

function serializeAdf(adf: ADFEntity | ADFEntity[]): string {
	return JSON.stringify(wrapAdfWithDocNode(adf));
}

export type AiSuggestions = {
	isAiSuggestionLoading: boolean;
	aiSuggestedName: string | null;
	aiSuggestedDescription: string | null;
};

/**
 * Fetches AI-generated name and description suggestions for a snippet.
 * Fires success/failure operational analytics with timing and payload details.
 */
export function useAiSuggestions(
	adf: ADFEntity | ADFEntity[] | undefined,
	origin: AnalyticsOrigin,
): AiSuggestions {
	const { cloudId } = useSessionData();
	const { fireFailedAnalytic, fireSuccessAnalytic } = useSnippetAnalytics(origin);

	const [isAiSuggestionLoading, setIsAiSuggestionLoading] = useState(false);
	const [aiSuggestedName, setAiSuggestedName] = useState<string | null>(null);
	const [aiSuggestedDescription, setAiSuggestedDescription] = useState<string | null>(null);

	useEffect(() => {
		if (!adf || !cloudId) {
			const error = !adf ? 'No adf' : 'No cloudId';
			fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_AI_METADATA, {
				error,
			});
			return;
		}

		setIsAiSuggestionLoading(true);
		const startTime = performance.now();
		const adfContent = serializeAdf(adf);
		const adfBodySize = adfContent.length;
		ConversationalAiClient.fetchSnippetMetadata({ adfContent }, cloudId)
			.then((response: SnippetMetadataResponse) => {
				const durationMs = Math.round(performance.now() - startTime);
				const suggestionCount = response.suggestions?.length ?? 0;

				if (!suggestionCount) {
					fireSuccessAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_AI_METADATA, {
						suggestionCount: 0,
						adfBodySize,
						durationMs,
					});
					return;
				}
				const bestSuggestion = pickBestSuggestion(response.suggestions);
				fireSuccessAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_AI_METADATA, {
					suggestionCount,
					confidences: response.suggestions.map((s) => s.confidence),
					suggestedNameLength: bestSuggestion.name?.length ?? 0,
					suggestedDescriptionLength: bestSuggestion.description?.length ?? 0,
					adfBodySize,
					durationMs,
				});
				setAiSuggestedName(bestSuggestion.name ?? '');
				setAiSuggestedDescription(bestSuggestion.description ?? '');
			})
			.catch((error) => {
				const durationMs = Math.round(performance.now() - startTime);
				fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_AI_METADATA, {
					error: error instanceof Error ? error.message : 'Unknown error',
					...(error instanceof SnippetMetadataError && { statusCode: error.statusCode }),
					adfBodySize,
					durationMs,
				});
				reportError(error);
			})
			.finally(() => {
				setIsAiSuggestionLoading(false);
			});
	}, [adf, cloudId, fireFailedAnalytic, fireSuccessAnalytic]);

	return { isAiSuggestionLoading, aiSuggestedName, aiSuggestedDescription };
}
